"use client";

import { m as motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import Link from "next/link";
import {
  Gauge,
  ShoppingBag,
  Code2,
  Wrench,
  Sparkles,
  Smartphone,
  Palette,
  PhoneCall,
  RotateCcw,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/utils/analytics";

type LocalizedText = { it: string; en: string };
type Need = {
  key: string;
  icon: LucideIcon;
  label: LocalizedText;
  details: LocalizedText;
  service: LocalizedText;
  blurb: LocalizedText;
};

const NEEDS: Need[] = [
  {
    key: "site",
    icon: Gauge,
    label: { it: "Un sito nuovo", en: "A new website" },
    details: {
      it: "Landing page · Sito vetrina · Sito su misura",
      en: "Landing page · Brochure site · Custom site",
    },
    service: { it: "Siti WordPress su misura", en: "Custom WordPress sites" },
    blurb: {
      it: "Un sito veloce e su misura, fatto per farti trovare e durare nel tempo.",
      en: "A fast, tailored site, built to be found and to last over time.",
    },
  },
  {
    key: "sell",
    icon: ShoppingBag,
    label: { it: "Vendere online", en: "Sell online" },
    details: {
      it: "WooCommerce · Shopify · E-commerce custom",
      en: "WooCommerce · Shopify · Custom store",
    },
    service: { it: "Ecommerce WooCommerce", en: "WooCommerce ecommerce" },
    blurb: {
      it: "Un negozio costruito o rifatto su WooCommerce, Shopify o in codice, con pagamenti e spedizioni a posto.",
      en: "A store built or rebuilt on WooCommerce, Shopify or in code, with payments and shipping sorted.",
    },
  },
  {
    key: "product",
    icon: Code2,
    label: { it: "Un prodotto su misura (MVP)", en: "A custom product (MVP)" },
    details: {
      it: "La prima versione della tua idea · Web app · SaaS",
      en: "The first version of your idea · Web app · SaaS",
    },
    service: { it: "Web app e MVP su misura", en: "Custom web apps and MVPs" },
    blurb: {
      it: "La prima versione concreta della tua idea: web app, SaaS, CRM leggero. Online e pronta a crescere.",
      en: "The first concrete version of your idea: web app, SaaS, lean CRM. Online and ready to grow.",
    },
  },
  {
    key: "nocode",
    icon: Wrench,
    label: { it: "Sistemare un progetto no-code", en: "Fix a no-code project" },
    details: {
      it: "Lovable · Bubble · v0 · FlutterFlow",
      en: "Lovable · Bubble · v0 · FlutterFlow",
    },
    service: {
      it: "Da progetto no-code a prodotto vero",
      en: "From no-code to a real product",
    },
    blurb: {
      it: "Prendo il tuo Lovable, Bubble o v0 e lo trasformo in un prodotto solido.",
      en: "I take your Lovable, Bubble or v0 and turn it into a solid product.",
    },
  },
  {
    key: "ai",
    icon: Sparkles,
    label: { it: "Automatizzare con l'AI", en: "Automate with AI" },
    details: {
      it: "Preventivatori · Configuratori · Automazioni",
      en: "Quote tools · Configurators · Automations",
    },
    service: { it: "Automazioni con l'AI", en: "Automations with AI" },
    blurb: {
      it: "Preventivatori, configuratori e flussi che girano da soli: tolgo il lavoro ripetitivo con l'AI.",
      en: "Quote tools, configurators and flows that run on their own: I remove repetitive work with AI.",
    },
  },
  {
    key: "mobile",
    icon: Smartphone,
    label: { it: "Un'app nativa per telefono", en: "A native phone app" },
    details: {
      it: "iOS · Android · Flutter cross-platform",
      en: "iOS · Android · Flutter cross-platform",
    },
    service: { it: "App mobile", en: "Mobile apps" },
    blurb: {
      it: "App per iOS e Android (Flutter cross-platform), quando il prodotto vive sul telefono.",
      en: "Apps for iOS and Android (Flutter cross-platform), when the product lives on the phone.",
    },
  },
  {
    key: "creative",
    icon: Palette,
    label: { it: "Qualcosa di creativo / 3D", en: "Something creative / 3D" },
    details: {
      it: "Branding · Lusso & moda · Esperienze 3D",
      en: "Branding · Luxury & fashion · 3D experiences",
    },
    service: { it: "Lab creativo / 3D", en: "Creative lab / 3D" },
    blurb: {
      it: "Branding, esperienze 3D e interazioni: per brand di lusso, moda e chi vuole distinguersi davvero.",
      en: "Branding, 3D experiences and interactions: for luxury, fashion and anyone who wants to stand out.",
    },
  },
];

export function ServiceFinder({
  isItalian,
  bookingLink,
}: {
  isItalian: boolean;
  bookingLink: string;
}) {
  const [sel, setSel] = useState<Need | null>(null);

  return (
    <div className="surface-card relative overflow-hidden p-8 md:p-10">
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative">
        <span className="mb-3 block font-mono text-label text-primary">
          {isItalian ? "Trova il servizio giusto" : "Find the right service"}
        </span>

        <AnimatePresence mode="wait">
          {!sel ? (
            <motion.div
              key="question"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
            >
              <h2 className="page-cta-title mb-2">
                {isItalian ? "Cosa ti serve?" : "What do you need?"}
              </h2>
              <p className="mb-7 max-w-lg text-body-lg text-muted-foreground">
                {isItalian
                  ? "Scegli il punto di partenza e ti dico da dove partirei."
                  : "Pick a starting point and I'll tell you where I'd start."}
              </p>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {NEEDS.map((n) => (
                  <button
                    key={n.key}
                    type="button"
                    onClick={() => {
                      setSel(n);
                      trackEvent("cta_click", {
                        cta_text: `finder:${n.key}`,
                        cta_section: "services_finder",
                      });
                    }}
                    className="flip-card h-24 text-left"
                  >
                    <div className="flip-inner">
                      <div className="flip-face flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-surface/50 p-4">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                          <n.icon className="h-5 w-5 text-primary" />
                        </span>
                        <span className="text-sm font-medium text-foreground">
                          {isItalian ? n.label.it : n.label.en}
                        </span>
                      </div>
                      <div className="flip-face flip-back flex flex-col justify-center rounded-xl border border-primary/45 bg-surface p-4">
                        <span className="mb-1.5 font-mono text-[0.62rem] uppercase tracking-wider text-primary">
                          {isItalian ? n.label.it : n.label.en}
                        </span>
                        <span className="text-sm leading-snug text-muted-foreground">
                          {isItalian ? n.details.it : n.details.en}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="answer"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
            >
              <p className="mb-2 font-mono text-sm text-muted-foreground">
                {isItalian ? "Per te partirei da" : "For you I'd start with"}
              </p>
              <div className="mb-5 flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow">
                  <sel.icon className="h-6 w-6 text-primary-foreground" />
                </span>
                <div>
                  <h2 className="font-display text-2xl font-bold md:text-3xl">
                    {isItalian ? sel.service.it : sel.service.en}
                  </h2>
                  <p className="mt-1 max-w-lg text-body text-muted-foreground">
                    {isItalian ? sel.blurb.it : sel.blurb.en}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button variant="hero" size="lg" asChild>
                  <Link
                    href={bookingLink}
                    onClick={() =>
                      trackEvent("book_call_click", {
                        tool: "tidycal",
                        location: "services_finder",
                      })
                    }
                  >
                    {isItalian ? "Prenota una call" : "Book a call"}
                    <PhoneCall className="h-4 w-4" />
                  </Link>
                </Button>
                <button
                  type="button"
                  onClick={() => setSel(null)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
                >
                  <RotateCcw className="h-4 w-4" />
                  {isItalian ? "Ricomincia" : "Start over"}
                </button>
              </div>
              <p className="mt-5 flex items-center gap-2 text-xs text-muted-foreground/80">
                <ArrowRight className="h-3.5 w-3.5 text-primary" />
                {isItalian
                  ? "Spesso un progetto unisce più servizi: lo definiamo insieme in call."
                  : "A project often combines services: we define it together on a call."}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
